import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormModel} from "../form/form.model.abstract";
import {AbstractControl, UntypedFormGroup, ValidationErrors} from "@angular/forms";
import {FormConfig} from "../form/form.config";
import {VerbrauchsanalyseForm} from "../verbrauchsanalyse/verbrauchsanalyse.form";
import {of, Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, map, switchMap} from "rxjs/operators";
import {AddressAutocompleteService, AddressSuggestion} from "../services/address-autocomplete.service";

/**
 * Component for a textfield with a live street-address autocomplete dropdown.
 */
@Component({
  selector: '[app-formfield-address-autocomplete]',
  templateUrl: './formfield.address-autocomplete.component.html',
  standalone: false
})
export class FormfieldAddressAutocompleteComponent implements OnInit, OnDestroy {
  /**
   * Formmodel of the value
   */
  @Input() formModel: FormModel;

  /**
   * form
   */
  @Input() form: UntypedFormGroup;

  /**
   * configuration of the whole form, used to read the postal code from a previous step
   */
  @Input() formConfig: FormConfig;

  /**
   * current step
   */
  @Input() step: string;

  /**
   * minimum length of the input before suggestions are requested
   */
  readonly minLength = 3;

  /**
   * time to wait after the user stopped typing, before requesting suggestions
   */
  readonly debounceMs = 300;

  /**
   * current list of suggestions
   */
  suggestions: AddressSuggestion[] = [];

  /**
   * the (trimmed) value that `suggestions` was actually fetched for. Used to tell a genuinely
   * empty/current result apart from a stale list left over from a previous, since-changed value
   * (e.g. while the debounce/request for the new value is still pending).
   */
  protected suggestionsQuery: string = null;

  /**
   * the suggestion the user picked from the dropdown. Reset on every raw keystroke that doesn't
   * simply extend it (see `isExtendingIncompleteStreet`), so an edited value is no longer
   * considered "selected" even if it still looks similar.
   */
  protected selectedSuggestion: AddressSuggestion = null;

  /**
   * true while `selectedSuggestion` was reconstructed from a previous visit to this step (we only
   * know its freeform text and postal code then, not e.g. its street number) rather than picked
   * fresh from the dropdown in this session. Checks that need the full suggestion data (like the
   * house-number check) don't apply to a restored value - it was already validated when it was
   * first selected, and a house number can't retroactively disappear the way a postal code match can.
   */
  protected selectedSuggestionIsRestored: boolean = false;

  /**
   * emits every value the user types, used to debounce/switchMap the autocomplete requests
   */
  protected input$: Subject<string> = new Subject<string>();

  /**
   * subscription of the input pipeline, cleaned up on destroy
   */
  protected inputSubscription: Subscription;

  /**
   * constructor
   * @param addressAutocompleteService
   */
  constructor(protected addressAutocompleteService: AddressAutocompleteService) {
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    this.inputSubscription = this.input$.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      switchMap(value => {
        const query = value.trim();
        const results = query.length >= this.minLength ? this.addressAutocompleteService.search(value, this.getPostalCode()) : of([]);
        return results.pipe(map(suggestions => ({query, suggestions})));
      })
    ).subscribe(({query, suggestions}) => {
      this.suggestionsQuery = query;
      // while the user is typing a house number after a confirmed-but-numberless street, Azure
      // often has no data for that specific number and just returns the same bare street again as
      // its best match - hide that one, since re-selecting it would wipe out the number they typed
      this.suggestions = suggestions.filter(suggestion => !(
        this.selectedSuggestion
        && !this.selectedSuggestion.streetNumber
        && suggestion.freeformAddress === this.selectedSuggestion.freeformAddress
      ));
    });

    const addressControl = this.form.get(this.formModel.id);
    if (addressControl.value) {
      // restore the "confirmed selection" state from a previous visit to this step, so the user
      // isn't forced to pick the same address again just because the component was recreated.
      // street/house number come from the sibling hidden fields (set via setAddressComponentFields
      // during the original selection), so isExtendingIncompleteStreet() keeps working on restore too.
      this.selectedSuggestion = {
        freeformAddress: addressControl.value,
        street: this.form.get(this.formModel.id + '_strasse')?.value ?? null,
        streetNumber: this.form.get(this.formModel.id + '_hausnummer')?.value ?? null,
        postalCode: this.form.get(this.getPostalCodeFieldId())?.value ?? null,
        municipality: null,
        position: null
      };
      this.selectedSuggestionIsRestored = true;
    }
    addressControl.addValidators(control => this.validateAddress(control));
    addressControl.updateValueAndValidity();
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy() {
    this.inputSubscription?.unsubscribe();
  }

  /**
   * Validates that a non-empty value was actually picked from the suggestion dropdown (not just
   * typed freely) and includes a house number, and that its postal code matches the one entered
   * in a previous step, if any.
   * @param control
   */
  validateAddress(control: AbstractControl): ValidationErrors | null {
    const value: string = (control.value ?? '').trim();
    if (value.length === 0) {
      // empty is valid, the field stays optional
      this.formModel.setErrorId(null);
      return null;
    }

    // an unedited selection: an Azure-confirmed suggestion, or a value restored from a previous visit
    if (this.selectedSuggestion && this.selectedSuggestion.freeformAddress === control.value) {
      if (!this.selectedSuggestionIsRestored && !this.selectedSuggestion.streetNumber) {
        this.formModel.setErrorId(this.formModel.id + '.missinghousenumber');
        return {invalid: true};
      }
      return this.validatePostalCodeMatch(this.selectedSuggestion.postalCode);
    }

    // a real, PLZ-matching street was picked and the user is (still) typing/editing the house
    // number by hand - whether the original pick already had a house number of its own or not.
    // Azure often doesn't have house-level typeahead data, especially for smaller towns, so we
    // don't require Azure to confirm the exact house number itself.
    if (this.selectedSuggestion && this.isExtendingIncompleteStreet(control.value)) {
      if (!this.hasHouseNumberSuffix(control.value)) {
        this.formModel.setErrorId(this.formModel.id + '.missinghousenumber');
        return {invalid: true};
      }
      return this.validatePostalCodeMatch(this.selectedSuggestion.postalCode);
    }

    // "mustselect" only applies while an up-to-date, non-empty suggestion list for this exact
    // value is actually being shown. Anything else (too short, still debouncing/loading, zero
    // results) is the same generic "not offered (yet)" case, regardless of what was typed.
    // suggestionsQuery holds the search query (city suffix stripped, see extractSearchQuery), so it
    // has to be compared against the same stripped form of the current value here.
    const suggestionsShownForCurrentValue = this.suggestionsQuery === this.extractSearchQuery(value) && this.suggestions.length > 0;
    this.formModel.setErrorId(this.formModel.id + (suggestionsShownForCurrentValue ? '.mustselect' : '.notfound'));
    return {invalid: true};
  }

  /**
   * Checks the postal code of a confirmed street/address against the one entered in a previous step.
   * @param suggestionPostalCode
   */
  protected validatePostalCodeMatch(suggestionPostalCode: string): ValidationErrors | null {
    const postalCode = this.getPostalCode();
    if (postalCode && suggestionPostalCode && String(suggestionPostalCode) !== String(postalCode)) {
      this.formModel.setErrorId(this.formModel.id + '.plzmismatch');
      return {invalid: true};
    }

    this.formModel.setErrorId(null);
    return null;
  }

  /**
   * Whether `value` is still built on the confirmed street from `selectedSuggestion` - either
   * exactly that street, or the street plus something after it (a house number being typed or
   * edited by hand, regardless of whether the original pick already had one of its own).
   * @param value
   */
  protected isExtendingIncompleteStreet(value: string): boolean {
    const street = this.selectedSuggestion?.street;
    if (!street) {
      return false;
    }
    return value === street || value.startsWith(street + ' ');
  }

  /**
   * Whether the part of `value` after the confirmed street name looks like it contains a house number.
   * @param value
   */
  protected hasHouseNumberSuffix(value: string): boolean {
    const suffix = value.slice(this.selectedSuggestion.street.length).trim();
    return /\d/.test(suffix);
  }

  /**
   * Extracts just the house-number token right after the confirmed street name, ignoring
   * anything further down the value (e.g. the ", PLZ City" part of a suggestion that already
   * included one). Returns null if the remainder doesn't start with a digit.
   * @param value
   */
  protected extractHouseNumber(value: string): string | null {
    const remainder = value.slice(this.selectedSuggestion.street.length).replace(/^\s+/, '');
    const match = remainder.match(/^[0-9][0-9a-zA-Z/-]*/);
    return match ? match[0] : null;
  }

  /**
   * Strips a trailing ", PLZ City" (or anything else after the first comma) before using `value` as
   * an Azure search query. Sending that suffix along - e.g. while editing the house number of an
   * already-picked full address - noticeably confuses Azure's fuzzy matching and can make it return
   * unrelated streets instead of (or ranked above) the one actually being typed.
   * @param value
   */
  protected extractSearchQuery(value: string): string {
    const commaIndex = value.indexOf(',');
    return (commaIndex >= 0 ? value.slice(0, commaIndex) : value).trim();
  }

  /**
   * Textkey suffix for a hint to show inside the (otherwise empty) suggestion dropdown, or null if
   * none applies. Only covers a confirmed street with no (recognized) house number typed yet - that
   * would otherwise show an empty/closed dropdown with no explanation while the user is still typing
   * (the error message below the field only appears once it's touched/left). A genuine "nothing
   * found" search deliberately has no dropdown hint - the validation error message covers that case.
   *
   * Doesn't apply to a value just restored from a previous visit: it was already a confirmed
   * selection back then, and nothing has actually been (re-)searched for it in this session yet -
   * an empty `suggestions` here just means no search has run, not that Azure doesn't know the address.
   */
  getDropdownHint(): string {
    if (this.suggestions.length > 0 || this.selectedSuggestionIsRestored) {
      return null;
    }

    const value = (this.form.get(this.formModel.id)?.value ?? '').trim();
    if (value.length === 0) {
      return null;
    }

    if (this.selectedSuggestion && this.isExtendingIncompleteStreet(value)) {
      return this.hasHouseNumberSuffix(value) ? 'unknownhousenumber' : 'enterhousenumber';
    }

    return null;
  }

  /**
   * Returns the postal code that was entered in a previous step, to bias the address search.
   */
  getPostalCode(): string {
    return (this.formConfig as VerbrauchsanalyseForm)?.anfrage?.verbrauchsanalyseInput?.postleitzahl ?? null;
  }

  /**
   * Id of the sibling hidden field that stores the postal code of the selected suggestion.
   */
  protected getPostalCodeFieldId(): string {
    return this.formModel.id + '_postalcode';
  }

  /**
   * Keeps the sibling hidden street/house-number fields in sync with the currently confirmed
   * address, used for the district heating address matching on submit (avoids re-parsing the
   * free-text 'adresse' string on the backend). `null` clears a field that isn't known (yet).
   */
  protected setAddressComponentFields(street: string | null, houseNumber: string | null): void {
    this.form.get(this.formModel.id + '_strasse')?.setValue(street);
    this.form.get(this.formModel.id + '_hausnummer')?.setValue(houseNumber);
  }

  /**
   * Eventhandler for typing in the input field. Any edit that isn't simply extending a confirmed,
   * numberless street (see `isExtendingIncompleteStreet`) invalidates the previous selection.
   * @param event
   */
  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const stillEditingConfirmedStreet = this.selectedSuggestion && this.isExtendingIncompleteStreet(value);

    if (!stillEditingConfirmedStreet) {
      this.selectedSuggestion = null;
      this.selectedSuggestionIsRestored = false;
      this.form.get(this.getPostalCodeFieldId())?.setValue(null);
      this.setAddressComponentFields(null, null);
    } else {
      // capture the house number as the user types/edits it after a confirmed street - whether
      // that street was originally picked with or without its own house number
      this.selectedSuggestionIsRestored = false;
      this.setAddressComponentFields(this.selectedSuggestion.street, this.extractHouseNumber(value));
    }

    // drop the previous (now stale) suggestions immediately, instead of waiting for the debounced
    // request for the new value to resolve
    this.suggestions = [];
    this.suggestionsQuery = null;
    this.input$.next(this.extractSearchQuery(value));
  }

  /**
   * Eventhandler for leaving the field. Silently clears a value that consists only of whitespace,
   * instead of showing an error for what is almost always an accidental keystroke. Also completes
   * a manually-typed house number with the postal code/city, to match the "Street Number, PLZ City"
   * format an Azure-confirmed suggestion would already have.
   */
  onBlur(): void {
    const control = this.form.get(this.formModel.id);
    if (control.value && control.value.trim().length === 0) {
      control.setValue('');
      return;
    }

    if (this.selectedSuggestion
      && this.isExtendingIncompleteStreet(control.value) && this.hasHouseNumberSuffix(control.value)
      && !control.value.includes(this.selectedSuggestion.postalCode)) {
      const cityText = [this.selectedSuggestion.postalCode, this.selectedSuggestion.municipality].filter(Boolean).join(' ');
      if (cityText) {
        control.setValue(`${control.value.trim()}, ${cityText}`);
      }
    }
  }

  /**
   * Eventhandler for selecting a suggestion from the dropdown. The form value itself is already
   * set by the [value] binding on the mat-option via the autocomplete's ControlValueAccessor.
   *
   * If the suggestion has no house number yet (a bare street match), the field is instead filled
   * with just the street name, ready for the user to type the house number right after it.
   * @param suggestion
   */
  onSuggestionSelected(suggestion: AddressSuggestion): void {
    this.selectedSuggestion = suggestion;
    this.selectedSuggestionIsRestored = false;
    this.form.get(this.getPostalCodeFieldId())?.setValue(suggestion.postalCode ?? null);
    this.suggestions = [];

    if (!suggestion.streetNumber) {
      // house number isn't known yet - it's filled in once the user types it (see onInput())
      this.setAddressComponentFields(suggestion.street, null);

      // MatAutocomplete/MatOption write the option's own [value] (the full freeform address) into
      // the control right after this handler runs, so overwriting it with just the street name has
      // to happen a tick later, or it gets clobbered by that assignment
      setTimeout(() => {
        const prefix = suggestion.street + ' ';
        const control = this.form.get(this.formModel.id);
        control.setValue(prefix);
        this.input$.next(prefix);

        const input = document.getElementById(this.formModel.id) as HTMLInputElement;
        input?.focus();
        input?.setSelectionRange(prefix.length, prefix.length);
      });
    } else {
      this.setAddressComponentFields(suggestion.street, suggestion.streetNumber);
    }
  }
}
